// ============================================================
// /mail-queue — Gmail 受信箱キュー（AIコントロールセンター テーマ2・S1 UI）
//
// 正: booking-platform/md/ai-control-center.md §13-2
// バックエンド: sys/mail/inbox_api.php（API_KEY_INTERNAL 認証）
//
// AI が S0 シャドウで出した decision（reply 案 / escalate）を人が処理する:
//   採用（そのまま送信）/ 編集して送信 / 独自に書いて送信 / 送信せず処理済み・対応不要
// 送信の宛先は**サーバ側で元メールの Reply-To/From に固定**（誤送信ガード）。
// 人の操作は outcome として decision に書き戻り、シャドウ学習の突合材料になる。
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Chip, IconButton, Button, LinearProgress,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButtonGroup, ToggleButton, Collapse, Alert, Snackbar,
    RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { LINE_SYNC_KEY } from '../config/instances';
import { getOperatorName } from './reconcile/systemApi';
import ReservationEditorPanel from '../components/editor2';
import { toEditorReservation } from '../lib/editorDataTransform';
import { saveDispatchReservation, fetchDispatchReservations, fetchDispatchVehicles } from '../api/dispatchService';
import { logEvent } from '../lib/eventLog';
import { useReservationStatuses } from '../hooks/useReservationStatuses';
import { BUILTIN_MAIL_TEMPLATES, buildBuiltinMail } from '../components/editor2/hooks/usePaymentEmail';
import { setPageContext, clearPageContext } from '../agent/pageContext';

const API_URL = process.env.REACT_APP_MAIL_API_URL || 'https://booking.trailernchu.co.jp/sys/mail/inbox_api.php';

interface QueueDecision {
    id: number;
    mail_id: string;
    category: string;
    ai_action: string;
    ai_reply: string | null;
    send_to: string | null;
    mail_subject: string | null;
    reservation_code: string | null;
    thread_id?: string | null;
    ai_confidence: number;
    alert_level: number;
    ai_reasoning: string | null;
    staff_action: string | null;
    staff_reply: string | null;
    outcome: string | null;
    resolve_note: string | null;
    evaluation_score: number | null;
    created_at: string;
    sender: string | null;
    subject: string | null;
    received_at: string | null;
    snippet: string | null;
    body: string | null;
}

interface Counts { pending: number; done: number; total: number }

interface InboxMail {
    id: number;
    mail_id: string;
    sender: string | null;
    subject: string | null;
    snippet: string | null;
    received_at: string | null;
    is_ota: number;
    ota_type: string | null;
    is_inbox?: number;
    is_starred?: number;
    is_unread?: number;
    decision_id: number | null;
    ai_action: string | null;
    category: string | null;
    outcome: string | null;
}

interface SenderAgg {
    address: string;
    cnt: number;
    last_received: string;
    last_subject: string;
    rule: string | null;
    rule_pattern?: string;
}

const RULE_LABELS: Record<string, string> = {
    ignore: '無視', escalate: 'エスカレ', ai: 'AI対象',
};

type View = 'pending' | 'done' | 'all' | 'inbox' | 'senders';

const ACTION_STYLE: Record<string, { label: string; color: string }> = {
    reply: { label: '返信案あり', color: '#10b981' },
    escalate: { label: '要対応', color: '#ef4444' },
    ignore: { label: '返信不要', color: '#94a3b8' },
};
const CATEGORY_LABELS: Record<string, string> = {
    reservation: '予約', inquiry: '問い合わせ', billing: '請求・支払',
    partner: '取引先', promo: '営業・広告', system: '通知', other: 'その他',
};
const OUTCOME_LABELS: Record<string, string> = {
    adopted: 'AI案を採用', edited: '編集して送信', own: '独自に返信',
    handled_offline: '別途対応済み', no_action: '対応不要',
};
const ALERT_COLORS = ['transparent', '#f59e0b', '#ef4444'];

async function api(action: string, params: Record<string, string> = {}, body?: any): Promise<any> {
    const qs = new URLSearchParams({ action, api_key: LINE_SYNC_KEY, ...params });
    const res = await fetch(`${API_URL}?${qs}`, body === undefined
        ? undefined
        : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const json = await res.json().catch(() => null);
    if (!json) throw new Error(`APIエラー (HTTP ${res.status})`);
    if (!json.success) throw new Error(json.error || 'APIエラー');
    return json;
}

// AI判断の可読化: 末尾に混入した画像判定JSON(画像判定[...]: {...})を箇条書きに畳む
function renderReasoning(text: string): React.ReactNode {
    if (!text) return null;
    // 「画像判定[ファイル名]: {json}」を抽出して見やすく
    const imgIdx = text.indexOf('画像判定[');
    let head = imgIdx >= 0 ? text.slice(0, imgIdx).trim() : text.trim();
    const chips: React.ReactNode[] = [];
    if (imgIdx >= 0) {
        const rest = text.slice(imgIdx);
        const re = /画像判定\[([^\]]+)\]:\s*(\{[\s\S]*?\})(?=画像判定\[|$)/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(rest)) !== null) {
            let parsed: any = null;
            try { parsed = JSON.parse(m[2]); } catch { /* 壊れていれば生表示 */ }
            const valid = parsed?.is_valid_evidence;
            chips.push(
                <Box key={m[1]} sx={{ mt: 0.5, p: 1, background: '#f8fafc', borderRadius: 1.5, borderLeft: '3px solid #a78bfa' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d28d9' }}>
                        🖼️ {m[1]}: {parsed?.document_type || '判定'}{' '}
                        {valid === true ? '✅有効' : valid === false ? '❌無効' : '❓判断不能'}
                    </Typography>
                    {parsed?.summary && <Typography variant="caption" display="block" sx={{ color: '#475569' }}>{parsed.summary}</Typography>}
                    {Array.isArray(parsed?.concerns) && parsed.concerns.length > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: '#b45309' }}>懸念: {parsed.concerns.join(' / ')}</Typography>
                    )}
                </Box>,
            );
        }
    }
    // 「指示: 」以降は対話履歴で見えるので判断本文からは畳む
    head = head.replace(/[\s\S]*/, (x) => { const i = x.indexOf('\n指示:'); return i >= 0 ? x.slice(0, i) : x; }).trim();
    return (
        <>
            {head && <Typography variant="body2" sx={{ color: '#475569', whiteSpace: 'pre-wrap' }}>{head}</Typography>}
            {chips}
        </>
    );
}

export default function MailQueue() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<QueueDecision[]>([]);
    const [mails, setMails] = useState<InboxMail[]>([]);
    const [mailTotal, setMailTotal] = useState(0);
    const [mailScope, setMailScope] = useState<'inbox' | 'all'>('inbox');
    const [senders, setSenders] = useState<SenderAgg[]>([]);
    const [counts, setCounts] = useState<Counts | null>(null);
    const [filter, setFilter] = useState<View>('inbox'); // 統合ビュー(受信メール)が既定 2026-08-08
    const [pendingRows, setPendingRows] = useState<QueueDecision[]>([]); // 受信メール統合ビューでの展開用
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [openBody, setOpenBody] = useState<Record<number, boolean>>({});
    const [confirmId, setConfirmId] = useState<number | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
    const [composeCode, setComposeCode] = useState('');
    const [composeInstruction, setComposeInstruction] = useState('');
    const [composing, setComposing] = useState(false);
    const [cardInstructions, setCardInstructions] = useState<Record<number, string>>({});
    const [draftingId, setDraftingId] = useState<number | null>(null);
    // 対話モード: auto=文脈判定 / data=予約データ操作 / mail=メール作成（明示指定の逃げ道）
    const [aiMode, setAiMode] = useState<Record<number, 'auto' | 'data' | 'mail'>>({});
    const [askMail, setAskMail] = useState<Record<number, boolean>>({}); // 「メールを作成しますか?」
    const [templates, setTemplates] = useState<{ name: string; subject: string }[]>([]);
    const [tplChoice, setTplChoice] = useState<Record<number, string>>({});
    const [saveTpl, setSaveTpl] = useState<{ decisionId: number; name: string; subject: string; body: string } | null>(null);
    const [cancelChoice, setCancelChoice] = useState<'yes' | 'no' | null>(null);
    const [cancelNote, setCancelNote] = useState('');
    const [carTypeChange, setCarTypeChange] = useState(''); // 送信時に適用する車種変更(コード。空=なし)
    const [statusChange, setStatusChange] = useState(''); // 送信時に適用するステータス変更(id。空=なし)。例: オファー送信→「オファー中」
    const [sendTarget, setSendTarget] = useState<'sender' | 'reservation'>('sender');
    const [resEmail, setResEmail] = useState<string | null>(null); // 紐づいた予約の顧客メール(確認ダイアログ用)
    const [groupInfo, setGroupInfo] = useState<{ count: number; codes: string[] } | null>(null); // 複数台グループ

    const OWN_DOMAIN_RE = /@(n-car\.jp|n-car\.info|trailernchu\.co\.jp|pit-rent\.com)$/i;   // 宮古の自社通知（pit-rent.com）も含める
    const [tplLang, setTplLang] = useState<'ja' | 'en' | 'tw' | 'kr'>('ja');
    interface Attachment { attachment_id: string; filename: string; mime: string; size: number; url: string }
    const [attachments, setAttachments] = useState<Record<number, Attachment[] | 'loading'>>({});
    const [judgingAtt, setJudgingAtt] = useState<string | null>(null);
    const [translations, setTranslations] = useState<Record<number, { subject: string; body: string } | 'loading'>>({});
    const [chatHistory, setChatHistory] = useState<Record<number, { instruction: string; body: string; message?: string }[]>>({});
    // AI が提案した予約変更（承認するまで適用されない）
    interface ProposedChanges { start_date?: string; end_date?: string; car_type?: string; status?: number }
    const [proposals, setProposals] = useState<Record<number, { changes: ProposedChanges; current: any } | null>>({});
    const [summaryDialog, setSummaryDialog] = useState<{ title: string; result: any } | 'loading' | null>(null);
    const [openCards, setOpenCards] = useState<Record<number, boolean>>({}); // 要対応カードの展開状態（既定=畳む）
    // 受信メール一覧での本文表示（要対応でなくても読めるように・mail_id キー）
    const [mailBodies, setMailBodies] = useState<Record<string, string | 'loading'>>({});
    const [mailAtts, setMailAtts] = useState<Record<string, Attachment[] | 'loading'>>({});

    const toggleMailBody = async (mailId: string) => {
        if (mailBodies[mailId] !== undefined) {
            setMailBodies((s) => { const c = { ...s }; delete c[mailId]; return c; });
            return;
        }
        setMailBodies((s) => ({ ...s, [mailId]: 'loading' }));
        try {
            const json = await api('mail', { mail_id: mailId });
            setMailBodies((s) => ({ ...s, [mailId]: json.mail?.body || '(本文なし)' }));
        } catch (e: any) {
            setMailBodies((s) => { const c = { ...s }; delete c[mailId]; return c; });
            setToast({ msg: `本文の取得に失敗: ${e.message}`, ok: false });
        }
    };

    const loadMailAttachments = async (mailId: string) => {
        setMailAtts((s) => ({ ...s, [mailId]: 'loading' }));
        try {
            const json = await api('attachments', { mail_id: mailId });
            setMailAtts((s) => ({ ...s, [mailId]: json.attachments || [] }));
            if (!(json.attachments || []).length) setToast({ msg: 'このメールに添付はありません', ok: true });
        } catch (e: any) {
            setMailAtts((s) => { const c = { ...s }; delete c[mailId]; return c; });
            setToast({ msg: `添付の取得に失敗: ${e.message}`, ok: false });
        }
    };
    // 「別途対応済み/対応不要」の理由入力（AI学習用）
    const [resolveDialog, setResolveDialog] = useState<{ row: QueueDecision; resolution: 'handled_offline' | 'no_action'; note: string } | null>(null);

    // 予約サイドパネル（LineChatBoard と同じ埋め込みパターン）
    const [selectedRes, setSelectedRes] = useState<any>(null);
    const [editingRes, setEditingRes] = useState<any>(null);
    const [vehiclesCache, setVehiclesCache] = useState<Record<string, any>>({});
    const [isSavingRes, setIsSavingRes] = useState(false);
    const { reservationStatusList, vehicleTypeList } = useReservationStatuses();
    const resCache = React.useRef<Record<string, any>>({});

    useEffect(() => {
        fetchDispatchVehicles().then((data: any[]) => {
            const m: Record<string, any> = {};
            (data || []).forEach((v) => { m[String(v.id)] = v; });
            setVehiclesCache(m);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        setEditingRes(selectedRes ? toEditorReservation(selectedRes) : null);
    }, [selectedRes]);

    // エージェントに「メールキュー・開いている予約」を伝える（ai-control-center.md §15）
    useEffect(() => {
        const r: any = selectedRes;
        setPageContext({
            page: 'mail-queue',
            selection: r ? {
                type: 'reservation',
                id: String(r.contact_code || r.id),
                label: `予約 ${r.contact_code || r.id} ${r.customer_name || ''} 様（メールキューから表示中）`,
            } : undefined,
        });
        return () => clearPageContext('mail-queue');
    }, [selectedRes]);

    // 予約コード → 完全な予約データ（キャッシュ付き）
    // ⚠️ 書き込み前は必ず fresh=true で取り直す。
    // 2026-08-11: キャッシュの古いデータで保存し、サーバが設定した status=4 を 1 に
    // 巻き戻す事故が起きた（ref-save-reservation.md の全量コピー原則の落とし穴）。
    const fetchReservationByCode = async (code: string, fresh = false): Promise<any | null> => {
        if (!fresh && resCache.current[code]) return resCache.current[code];
        const list = await fetchDispatchReservations({ contactCode: code });
        const hit = (list || []).find((r: any) => String(r.contact_code) === code) || null;
        if (hit) resCache.current[code] = hit;
        return hit;
    };

    // 複数台予約(LP のみ)は contact_code に枝番が付く: {base}-1 … {base}-N
    // 正: booking-platform/md/multi-vehicle.md / new-n-car ReservationForm.jsx
    const groupBaseOf = (code: string) => code.replace(/-\d+$/, '');

    /** グループ全件を取得（backend の contact_code 検索は前方一致・実測 2026-08-09） */
    const fetchGroupReservations = async (code: string): Promise<any[]> => {
        const base = groupBaseOf(code);
        const list = await fetchDispatchReservations({ contactCode: base });
        const members = (list || []).filter((r: any) => {
            const c = String(r.contact_code || '');
            return c === base || c.startsWith(base + '-');
        });
        members.sort((a: any, b: any) => String(a.contact_code).localeCompare(String(b.contact_code)));
        return members;
    };

    const openReservation = async (code: string) => {
        try {
            const hit = await fetchReservationByCode(code);
            if (!hit) { setToast({ msg: `予約 ${code} が見つかりません`, ok: false }); return; }
            setSelectedRes(hit);
        } catch (e: any) {
            setToast({ msg: `予約の取得に失敗: ${e.message}`, ok: false });
        }
    };

    const load = useCallback(async (f: View = filter) => {
        setLoading(true);
        try {
            if (f === 'inbox') {
                // 表示前に Gmail と同期（受信トレイ・スター・未読の完全ミラー）
                await api('sync').catch(() => {});
                const [json, q] = await Promise.all([
                    api('mails', { limit: '100', scope: mailScope }),
                    api('queue', { filter: 'pending', limit: '50' }),
                ]);
                setMails(json.mails || []);
                setMailTotal(json.total || 0);
                setPendingRows(q.decisions || []);
                setCounts(q.counts || null);
            } else if (f === 'senders') {
                const json = await api('senders', { limit: '50' });
                setSenders(json.senders || []);
            } else {
                const json = await api('queue', { filter: f, limit: '50' });
                setRows(json.decisions || []);
                setCounts(json.counts || null);
            }
        } catch (e: any) {
            setToast({ msg: `読み込み失敗: ${e.message}`, ok: false });
        }
        setLoading(false);
    }, [filter, mailScope]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api('templates').then((j) => setTemplates(j.templates || [])).catch(() => {});
    }, []);

    // エージェント（フロートパネル）がメールを操作したら一覧に即反映する（2026-09-01）。
    // サーバ側は archive の時点で is_inbox=0 / decision クローズまで済ませているが、
    // この画面は自分の操作でしか再読込しないため「アーカイブしたのに残って見える」状態だった。
    // まず楽観更新で見た目を合わせ、そのあと load() で実データに揃える。
    useEffect(() => {
        const onMailChanged = (e: Event) => {
            const d = (e as CustomEvent).detail || {};
            const mid = String(d.mail_id || '');
            if (mid) {
                setMails((cur) => cur.map((m) => (
                    m.mail_id === mid ? { ...m, is_inbox: 0, is_unread: 0 } : m
                )));
                setPendingRows((cur) => cur.filter((r) => r.mail_id !== mid));
            }
            load();
        };
        window.addEventListener('mail-queue:changed', onMailChanged);
        return () => window.removeEventListener('mail-queue:changed', onMailChanged);
    }, [load]);

    // Gmail から手動取り込み → ワーカー完了を少し待って再読込
    const manualFetch = async () => {
        setFetching(true);
        try {
            await api('fetch', { limit: '30' });
            setToast({ msg: '取り込みを開始しました（数秒後に自動更新）', ok: true });
            setTimeout(() => { load(); setFetching(false); }, 8000);
        } catch (e: any) {
            setToast({ msg: `取り込み失敗: ${e.message}`, ok: false });
            setFetching(false);
        }
    };

    const draftOf = (r: QueueDecision) => drafts[r.id] ?? (r.ai_reply || '');

    // 送信確認ダイアログで「予約をキャンセルにしますか?」を出す条件（紐づけ済み + キャンセル文脈）。
    // backend が既にキャンセル済み(status=4)と分かっている場合は質問しない（無駄な確認の削減）
    const asksCancel = (r: QueueDecision) =>
        !!r.reservation_code
        && /キャンセル/.test(r.ai_reasoning || '')
        && !/既にキャンセル済み|status=4/.test(r.ai_reasoning || '');

    // 予約変更の適用（オファー送信時のステータス変更・了承時の車種変更など）。
    // **中央経路 saveDispatchReservation + 全量コピー**のみ。
    // 車両の自動アサインはしない（空き判定SSOT未確定のため）— 適用後にパネルを開いて人が確認する
    /**
     * 予約変更を適用する。**複数台グループ({base}-1..N)は全件にまとめて適用**する
     * (2026-08-09: LP の複数台リクエストをオファーに変更する運用。
     *  ただし車種はグループ内で台ごとに異なるため、車種変更だけは対象を1件に限定する)
     */
    const applyReservationChanges = async (
        r: QueueDecision,
        changes: { carType?: string; statusId?: string; startDate?: string; endDate?: string },
    ) => {
        const code = r.reservation_code!;
        const group = await fetchGroupReservations(code);
        if (!group.length) throw new Error(`予約 ${code} が見つかりません`);
        // 車種変更は台ごとに異なるため単一予約のみ（完全一致の1件）に適用する
        const targets = changes.carType
            ? group.filter((x: any) => String(x.contact_code) === code)
            : group;
        if (!targets.length) throw new Error(`予約 ${code} が見つかりません（車種変更は枝番を指定してください）`);

        const savedList: any[] = [];
        for (const full of targets) {
            const saved = await saveDispatchReservation({
                ...full,
                ...(changes.carType ? { car_type: changes.carType } : {}),
                ...(changes.statusId ? { status: Number(changes.statusId) } : {}),
                ...(changes.startDate ? { start_date: changes.startDate } : {}),
                ...(changes.endDate ? { end_date: changes.endDate } : {}),
            });
            resCache.current[String(full.contact_code)] = saved;
            savedList.push(saved);
            logEvent({
                action: 'mail.reservation.update', target: String(full.contact_code), result: 'ok',
                detail: {
                    car_type: changes.carType ? { from: full.car_type, to: changes.carType } : undefined,
                    status: changes.statusId ? { from: full.status, to: Number(changes.statusId) } : undefined,
                    start_date: changes.startDate ? { from: full.start_date, to: changes.startDate } : undefined,
                    end_date: changes.endDate ? { from: full.end_date, to: changes.endDate } : undefined,
                    decision_id: r.id, group_size: targets.length,
                },
            } as any);
        }
        return { saved: savedList[0], count: savedList.length, codes: targets.map((x: any) => String(x.contact_code)) };
    };

    // AI 提案の承認 → 中央経路で適用 + 予約メモに監査記録
    const approveProposal = async (r: QueueDecision) => {
        const p = proposals[r.id];
        if (!p) return;
        try {
            const res = await applyReservationChanges(r, {
                startDate: p.changes.start_date, endDate: p.changes.end_date,
                carType: p.changes.car_type, statusId: p.changes.status != null ? String(p.changes.status) : undefined,
            });
            const saved = res.saved;
            const parts: string[] = [];
            if (p.changes.start_date) parts.push(`貸出→${p.changes.start_date}`);
            if (p.changes.end_date) parts.push(`返却→${p.changes.end_date}`);
            if (p.changes.car_type) parts.push(`車種→${p.changes.car_type}`);
            if (p.changes.status != null) parts.push(`ステータス→${reservationStatusList.find((st: any) => st.id === p.changes.status)?.label || p.changes.status}`);
            api('memo', {}, {
                reservation_code: r.reservation_code, operator: getOperatorName(),
                line: `---予約変更 ${getOperatorName() || 'mail-queue'}(AI提案を承認) ${new Date().toLocaleString('ja-JP')}---\n${parts.join(' / ')}（メール依頼 decision#${r.id}）`,
            }).catch(() => {});
            setProposals((s) => ({ ...s, [r.id]: null }));
            setChatHistory((s) => ({ ...s, [r.id]: [...(s[r.id] || []), { instruction: '(変更を承認)', body: draftOf(r), message: `予約変更を適用しました: ${parts.join(' / ')}` }] }));
            setToast({
                msg: `予約を変更しました（${parts.join(' / ')}）`
                    + (res.count > 1 ? ` — 複数台グループ ${res.count}件すべてに適用: ${res.codes.join(' / ')}` : '')
                    + '。内容を確認してください',
                ok: true,
            });
            setSelectedRes(saved);
        } catch (e: any) {
            setToast({ msg: `⚠予約変更に失敗: ${e.message}`, ok: false });
        }
    };

    const send = async (r: QueueDecision) => {
        setBusyId(r.id);
        try {
            const json = await api('send_reply', {}, {
                decision_id: r.id, body: draftOf(r), operator: getOperatorName(),
                ...(r.reservation_code && !r.send_to ? { send_target: sendTarget } : {}),
                ...(asksCancel(r) ? {
                    cancel_reservation: cancelChoice === 'yes',
                    cancel_note: cancelChoice === 'no' ? cancelNote : '',
                } : {}),
            });
            const extras = [
                json.memo_appended === true ? '予約メモ記録済み' : json.memo_appended === false ? '⚠メモ記録失敗' : '',
                json.reservation_cancelled === true ? '予約をキャンセル済みに変更' :
                    json.reservation_cancelled === 'already' ? '既にキャンセル済み(変更なし)' :
                    json.reservation_cancelled === false && cancelChoice === 'yes' ? '⚠キャンセル変更失敗' :
                    cancelChoice === 'no' ? 'ステータス変更なし(理由記録)' : '',
                json.archived === true ? 'アーカイブ済み' : json.archived === false ? `⚠アーカイブ失敗: ${json.archive_note || ''}` : '',
            ].filter(Boolean).join(' / ');
            setToast({
                msg: `送信しました → ${json.sent_to}（${OUTCOME_LABELS[json.outcome] || json.outcome}）${extras ? ` / ${extras}` : ''}`,
                ok: true,
            });
            // Square 請求書の取消結果（サーバが同一 POST で処理）
            if (json.invoice === 'voided') {
                setToast({ msg: 'Square請求書も取り消しました', ok: true });
            } else if (json.invoice === 'failed') {
                setToast({ msg: `⚠ Square請求書の取消が残っています: ${json.invoice_msg || ''} — Square 管理画面で対応してください（予約メモにも記録済み）`, ok: false });
            }

            // 予約変更の適用（指定時）: 中央経路で保存 → 確認のためパネルを開く
            if ((carTypeChange || statusChange) && r.reservation_code) {
                try {
                    const res = await applyReservationChanges(r, {
                        carType: carTypeChange || undefined, statusId: statusChange || undefined,
                    });
                    const parts = [
                        carTypeChange ? `車種→「${vehicleTypeList.find((vt: any) => vt.code === carTypeChange)?.label || carTypeChange}」` : '',
                        statusChange ? `ステータス→「${reservationStatusList.find((st: any) => String(st.id) === statusChange)?.label || statusChange}」` : '',
                    ].filter(Boolean).join(' / ');
                    setToast({
                        msg: `予約を変更しました（${parts}）`
                            + (res.count > 1 ? ` — 複数台グループ ${res.count}件すべてに適用: ${res.codes.join(' / ')}` : '')
                            + '。内容を確認してください',
                        ok: true,
                    });
                    setSelectedRes(res.saved);
                } catch (e: any) {
                    setToast({ msg: `⚠予約変更に失敗: ${e.message}（パネルから手動で変更してください）`, ok: false });
                }
            }
            load();
        } catch (e: any) {
            setToast({ msg: `送信失敗: ${e.message}`, ok: false });
        }
        setBusyId(null);
        setConfirmId(null);
    };

    const resolve = async (r: QueueDecision, resolution: 'handled_offline' | 'no_action', note = '') => {
        setBusyId(r.id);
        try {
            const j = await api('resolve', {}, { decision_id: r.id, resolution, note, operator: getOperatorName() });
            setToast({
                msg: OUTCOME_LABELS[resolution] + ' にしました' + (note ? '（理由を記録）' : '')
                    + (j.archived ? ' / アーカイブ済み' : j.archive_note ? ` / ⚠アーカイブ失敗: ${j.archive_note}` : ''),
                ok: true,
            });
            load();
        } catch (e: any) {
            setToast({ msg: `更新失敗: ${e.message}`, ok: false });
        }
        setBusyId(null);
    };

    // decision カードの描画（要対応タブと受信メール統合ビューの両方から使う）
    const renderCard = (r: QueueDecision) => {
                    const act = ACTION_STYLE[r.ai_action] || { label: r.ai_action, color: '#e2e8f0' };
                    const pending = r.outcome === null && (r.ai_action === 'reply' || r.ai_action === 'escalate');
                    return (
                        <Paper key={r.id} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                            {/* Card header */}
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    {r.received_at ? new Date(r.received_at.replace(/-/g, '/')).toLocaleString('ja-JP') : ''}
                                </Typography>
                                <Chip label={act.label} size="small"
                                    sx={{ background: act.color, color: 'white', fontWeight: 800, fontSize: '10px', height: 22 }} />
                                <Chip label={CATEGORY_LABELS[r.category] || r.category} size="small" variant="outlined"
                                    sx={{ fontWeight: 700, fontSize: '10px', height: 22 }} />
                                {r.alert_level > 0 && (
                                    <Chip label={`Alert ${r.alert_level}`} size="small"
                                        sx={{ background: ALERT_COLORS[r.alert_level] || '#ef4444', color: 'white', fontWeight: 800, fontSize: '10px', height: 22 }} />
                                )}
                                {r.reservation_code && (
                                    <Chip label={`予約 ${r.reservation_code}`} size="small" variant="outlined"
                                        icon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                                        onClick={() => openReservation(r.reservation_code!)}
                                        sx={{ fontWeight: 700, fontSize: '10px', height: 22, borderColor: '#3b82f6', color: '#3b82f6', cursor: 'pointer' }} />
                                )}
                                {r.reservation_code && (
                                    /* 予約セッション（§16 S3）: この予約の作業履歴つきでエージェントと対話する */
                                    <Chip label="AIと対話" size="small"
                                        icon={<SmartToyIcon sx={{ fontSize: 13 }} />}
                                        onClick={() => window.dispatchEvent(new CustomEvent('agent:open-reservation-session', {
                                            detail: {
                                                contact_code: r.reservation_code,
                                                // このメールが起点だとエージェントに分かるように文脈を渡す（§16 S3b）
                                                mail: {
                                                    decision_id: r.id,
                                                    mail_id: r.mail_id || '',   // スレッド返信の宛先（記録#10）
                                                    sender: r.sender || '',
                                                    subject: r.subject || r.mail_subject || '',
                                                    snippet: (r.snippet || '').slice(0, 300),
                                                },
                                            },
                                        }))}
                                        sx={{ fontWeight: 700, fontSize: '10px', height: 22,
                                              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                              color: 'white', cursor: 'pointer', '& .MuiChip-icon': { color: 'white' } }} />
                                )}
                                {r.outcome && (
                                    <Chip label={OUTCOME_LABELS[r.outcome] || r.outcome} size="small"
                                        sx={{ background: '#0ea5e9', color: 'white', fontWeight: 800, fontSize: '10px', height: 22 }} />
                                )}
                                <Typography variant="caption" fontWeight={700} ml="auto" sx={{ color: '#64748b' }}>
                                    conf {(Number(r.ai_confidence) * 100).toFixed(0)}%
                                </Typography>
                            </Box>

                            {/* Mail */}
                            <Box sx={{ mt: 1, p: 1.5, background: '#f1f5f9', borderRadius: 2, borderLeft: '3px solid #3b82f6' }}>
                                <Typography variant="body2" fontWeight={700}>
                                    {r.subject || (r.mail_subject ? `[起案] ${r.mail_subject}` : '(件名なし)')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    {r.sender || (r.send_to ? `宛先: ${r.send_to}（予約 ${r.mail_id.replace('rsv:', '')}）` : '')}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, color: '#334155' }}>
                                    {openBody[r.id] ? (r.body || r.snippet || '') : (r.snippet || '')}
                                </Typography>
                                {/* 全文表示中は添付画像を文中に自動インライン表示 */}
                                {openBody[r.id] && Array.isArray(attachments[r.id]) &&
                                    (attachments[r.id] as Attachment[])
                                        .filter((a) => a.mime.startsWith('image/'))
                                        .map((a) => (
                                            <Box key={a.attachment_id} sx={{ mt: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>📎 {a.filename}</Typography>
                                                <img src={a.url} alt={a.filename}
                                                    style={{ display: 'block', maxWidth: '100%', maxHeight: 420, borderRadius: 8, cursor: 'zoom-in' }}
                                                    onClick={() => openAttachment(a)} />
                                            </Box>
                                        ))}
                                {/* 日本語訳 */}
                                {translations[r.id] && translations[r.id] !== 'loading' && (
                                    <Box sx={{ mt: 1, p: 1.5, background: '#ecfdf5', borderRadius: 2, borderLeft: '3px solid #10b981' }}>
                                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700 }}>
                                            日本語訳: {(translations[r.id] as any).subject}
                                        </Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                            {(translations[r.id] as any).body}
                                        </Typography>
                                    </Box>
                                )}
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                {r.body && r.body !== r.snippet && (
                                    <Button size="small" sx={{ mt: 0.5, fontSize: 11 }}
                                        startIcon={openBody[r.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        onClick={() => {
                                            const opening = !openBody[r.id];
                                            setOpenBody((s) => ({ ...s, [r.id]: opening }));
                                            if (opening && attachments[r.id] === undefined && !r.send_to) loadAttachments(r);
                                        }}>
                                        {openBody[r.id] ? '閉じる' : '全文を表示'}
                                    </Button>
                                )}
                                {!r.send_to && translations[r.id] === undefined && (
                                    <Button size="small" sx={{ mt: 0.5, fontSize: 11 }} onClick={() => translate(r)}>
                                        🌐 日本語訳
                                    </Button>
                                )}
                                {translations[r.id] === 'loading' && (
                                    <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1 }}>翻訳中…</Typography>
                                )}
                                {senderAddress(r) && (
                                    <Button size="small" sx={{ mt: 0.5, fontSize: 11 }}
                                        onClick={() => summarize('email', senderAddress(r)!, `差出人 ${senderAddress(r)}`)}>
                                        📋 メールアドレスで概要化
                                    </Button>
                                )}
                                {r.reservation_code && (
                                    <Button size="small" sx={{ mt: 0.5, fontSize: 11 }}
                                        onClick={() => summarize('code', r.reservation_code!, `予約 ${r.reservation_code}`)}>
                                        📋 予約コードで概要化
                                    </Button>
                                )}
                                </Box>
                                {!r.send_to && (
                                    <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap" alignItems="center">
                                        {attachments[r.id] === undefined && (
                                            <Button size="small" sx={{ fontSize: 11 }} onClick={() => loadAttachments(r)}>
                                                📎 添付を表示
                                            </Button>
                                        )}
                                        {attachments[r.id] === 'loading' && (
                                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>添付を確認中…</Typography>
                                        )}
                                        {Array.isArray(attachments[r.id]) && (attachments[r.id] as Attachment[]).map((a) => (
                                            <Box key={a.attachment_id} display="flex" gap={0.25} alignItems="center">
                                                <Chip size="small" label={`📎 ${a.filename}`} clickable
                                                    onClick={() => openAttachment(a)}
                                                    sx={{ fontSize: 11, height: 22 }} />
                                                {/^(image\/|application\/pdf)/.test(a.mime) && (
                                                    <Button size="small" sx={{ fontSize: 10, minWidth: 0, px: 0.75 }}
                                                        disabled={judgingAtt === a.attachment_id}
                                                        onClick={() => judgeAttachment(r, a)}>
                                                        {judgingAtt === a.attachment_id ? '判定中…' : 'AI判定'}
                                                    </Button>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* AI判断（画像判定JSONは箇条書きに整形） */}
                            {r.ai_reasoning && (
                                <Box sx={{ mt: 0.75, p: 1, background: '#faf5ff', borderRadius: 2, borderLeft: '3px solid #c4b5fd' }}>
                                    <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700, display: 'block', mb: 0.25 }}>AI判断</Typography>
                                    {renderReasoning(r.ai_reasoning)}
                                </Box>
                            )}
                            {/* 予約の手動紐づけ（根拠コメント付き。人の判断根拠は AI 自律紐づけの教師データになる） */}
                            {pending && !r.send_to && (
                                <Box display="flex" gap={0.5} alignItems="center" mt={0.5} flexWrap="wrap">
                                    <TextField size="small" placeholder="予約コードで紐づけ"
                                        defaultValue={r.reservation_code || ''}
                                        id={`link-code-${r.id}`}
                                        sx={{ width: 180, '& .MuiInputBase-root': { fontSize: 12 } }} />
                                    <TextField size="small" placeholder="根拠（例: メールアドレス一致）"
                                        id={`link-note-${r.id}`}
                                        sx={{ width: 220, '& .MuiInputBase-root': { fontSize: 12 } }} />
                                    <Button size="small" variant="outlined" sx={{ fontSize: 11, py: 0.2 }}
                                        onClick={async () => {
                                            const code = (document.getElementById(`link-code-${r.id}`) as HTMLInputElement)?.value.trim().toUpperCase() || '';
                                            const note = (document.getElementById(`link-note-${r.id}`) as HTMLInputElement)?.value.trim() || '';
                                            try {
                                                await api('link', {}, { decision_id: r.id, reservation_code: code, note, operator: getOperatorName() });
                                                setToast({ msg: code ? `予約 ${code} に紐づけました${note ? `（根拠: ${note}）` : ''}` : '紐づけを解除しました', ok: true });
                                                load();
                                            } catch (err: any) { setToast({ msg: `紐づけ失敗: ${err.message}`, ok: false }); }
                                        }}>
                                        紐づけ
                                    </Button>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>空欄で解除</Typography>
                                </Box>
                            )}

                            {/* 対話型の指示 → AI起案（受信メール起点のカードのみ） */}
                            {/* AI の予約変更提案（承認して初めて適用。価格系は構造的に不可） */}
                            {pending && proposals[r.id] && (() => {
                                const p = proposals[r.id]!;
                                const cur = p.current || {};
                                const fmt = (v: any) => v ? String(v).replace('T', ' ').slice(0, 16) : '—';
                                const rows2: [string, string, string][] = [];
                                if (p.changes.start_date) rows2.push(['貸出日時', fmt(cur.start_date), p.changes.start_date]);
                                if (p.changes.end_date) rows2.push(['返却日時', fmt(cur.end_date), p.changes.end_date]);
                                if (p.changes.car_type) rows2.push(['車種', String(cur.car_type || '—'), p.changes.car_type]);
                                if (p.changes.status != null) rows2.push(['ステータス',
                                    String(reservationStatusList.find((st: any) => String(st.id) === String(cur.status))?.label || cur.status || '—'),
                                    String(reservationStatusList.find((st: any) => st.id === p.changes.status)?.label || p.changes.status)]);
                                return (
                                    <Box sx={{ mt: 1.5, p: 1.5, background: '#fff7ed', borderRadius: 2, border: '2px solid #fb923c' }}>
                                        <Typography variant="body2" fontWeight={800} sx={{ color: '#c2410c', mb: 0.5 }}>
                                            🤖 AI の予約変更提案（承認するまで適用されません）
                                        </Typography>
                                        {rows2.map(([label, from, to]) => (
                                            <Typography key={label} variant="body2" sx={{ color: '#431407' }}>
                                                {label}: {from} → <b>{to}</b>
                                            </Typography>
                                        ))}
                                        <Box display="flex" gap={1} mt={1}>
                                            <Button size="small" variant="contained" color="warning" onClick={() => approveProposal(r)}>
                                                承認して変更
                                            </Button>
                                            <Button size="small" color="inherit" onClick={() => {
                                                setProposals((s) => ({ ...s, [r.id]: null }));
                                                setChatHistory((s) => ({ ...s, [r.id]: [...(s[r.id] || []), { instruction: '(変更を却下)', body: draftOf(r), message: '変更提案は破棄されました' }] }));
                                            }}>
                                                却下
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            })()}

                            {pending && askMail[r.id] && (
                                <Box sx={{ mt: 1, p: 1.25, background: '#ecfeff', borderRadius: 2, border: '1px solid #67e8f9' }}
                                    display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#0e7490' }}>
                                        メールを作成しますか？
                                    </Typography>
                                    <Button size="small" variant="contained" color="info"
                                        disabled={draftingId === r.id}
                                        onClick={() => { setAskMail((s) => ({ ...s, [r.id]: false }));
                                            draftReply(r, { instruction: 'ここまでの内容でお客様へ送るメール本文を作成してください', mode: 'mail' }); }}>
                                        はい
                                    </Button>
                                    <Button size="small" color="inherit"
                                        onClick={() => setAskMail((s) => ({ ...s, [r.id]: false }))}>
                                        いいえ
                                    </Button>
                                </Box>
                            )}

                            {pending && !r.send_to && (chatHistory[r.id] || []).length > 0 && (
                                <Box sx={{ mt: 1.5, p: 1, background: '#f8fafc', borderRadius: 2 }}>
                                    {(chatHistory[r.id] || []).map((h, i) => (
                                        <Box key={i} sx={{ mb: 0.5 }}>
                                            <Typography variant="caption" display="block" sx={{ color: '#334155', fontWeight: 700 }}>
                                                💬 {h.instruction}
                                            </Typography>
                                            {h.message && (
                                                <Typography variant="caption" display="block"
                                                    sx={{ color: '#7c3aed', whiteSpace: 'pre-wrap', pl: 2, borderLeft: '2px solid #ddd6fe', ml: 0.5 }}>
                                                    🤖 {h.message}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {pending && !r.send_to && (
                                <Box sx={{ mt: 1.5 }}>
                                    {/* 1行目: AIへの指示（対話。既定は予約データの操作） */}
                                    <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700 }}>
                                        AIに指示（対話）
                                    </Typography>
                                    <TextField size="small" fullWidth multiline maxRows={4}
                                        placeholder={(chatHistory[r.id] || []).length > 0
                                            ? '例: 返却も18時に変更して / もう少しカジュアルに / メールを作成して'
                                            : '例: 貸出を14時に変更して / この内容で問題ないか確認して / メールを作成して'}
                                        value={cardInstructions[r.id] || ''}
                                        onChange={(e) => setCardInstructions((s) => ({ ...s, [r.id]: e.target.value }))}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && (cardInstructions[r.id] || '').trim()) {
                                                e.preventDefault(); draftReply(r);
                                            }
                                        }}
                                        sx={{ mt: 0.5, '& .MuiInputBase-root': { fontSize: 13 } }} />
                                    <Box display="flex" gap={1} mt={0.75} alignItems="center" flexWrap="wrap">
                                        <ToggleButtonGroup exclusive size="small" value={aiMode[r.id] || 'auto'}
                                            onChange={(_, v) => { if (v) setAiMode((s) => ({ ...s, [r.id]: v })); }}
                                            sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: 11, whiteSpace: 'nowrap' } }}>
                                            <ToggleButton value="auto">自動</ToggleButton>
                                            <ToggleButton value="data">データ変更</ToggleButton>
                                            <ToggleButton value="mail">メール作成</ToggleButton>
                                        </ToggleButtonGroup>
                                        <Button size="small" variant="contained" sx={{ whiteSpace: 'nowrap' }}
                                            disabled={draftingId === r.id || !(cardInstructions[r.id] || '').trim()}
                                            onClick={() => draftReply(r)}>
                                            {draftingId === r.id ? '実行中…' : 'AIに指示'}
                                        </Button>
                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>⌘/Ctrl+Enter</Typography>
                                    </Box>

                                    {/* 2行目: メール作成（対話を経由せず直接作る導線） */}
                                    <Box display="flex" gap={1} mt={1} alignItems="center" flexWrap="wrap"
                                        sx={{ pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                                        <Typography variant="caption" sx={{ color: '#0e7490', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            メール作成
                                        </Typography>
                                        <Button size="small" variant="outlined" color="info" sx={{ whiteSpace: 'nowrap' }}
                                            disabled={draftingId === r.id}
                                            onClick={() => draftReply(r, {
                                                instruction: (cardInstructions[r.id] || '').trim()
                                                    || 'このメールへの返信本文を作成してください',
                                                mode: 'mail',
                                            })}>
                                            {draftingId === r.id ? '作成中…' : '✉ AIで作成'}
                                        </Button>
                                        <TextField select size="small" label="テンプレから" value={tplChoice[r.id] || ''}
                                            SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setTplChoice((s) => ({ ...s, [r.id]: v }));
                                                if (v.startsWith('builtin:')) applyBuiltin(r, v.slice(8));
                                                else if (v) applyTemplate(r, v);
                                            }}
                                            sx={{ minWidth: 200 }}>
                                            <option value="">選択…</option>
                                            {r.reservation_code && (
                                                <optgroup label="定型（サイドパネル共通）">
                                                    {BUILTIN_MAIL_TEMPLATES.map((tp) => (
                                                        <option key={tp.key} value={`builtin:${tp.key}`}>{tp.label}</option>
                                                    ))}
                                                </optgroup>
                                            )}
                                            {templates.length > 0 && (
                                                <optgroup label="保存テンプレ">
                                                    {templates.map((tp) => (
                                                        <option key={tp.name} value={tp.name}>{tp.name}</option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </TextField>
                                        <TextField select size="small" label="言語" value={tplLang}
                                            SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                                            onChange={(e) => setTplLang(e.target.value as any)}
                                            sx={{ width: 110 }}>
                                            <option value="ja">日本語</option>
                                            <option value="en">English</option>
                                            <option value="tw">繁體</option>
                                            <option value="kr">한국어</option>
                                        </TextField>
                                    </Box>
                                </Box>
                            )}

                            {/* 返信エディタ（未処理のみ） */}
                            {pending && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700 }}>
                                        返信文（{draftOf(r).trim() !== '' ? '編集可 — 対話で AI に直させることも可能' : '未作成 — 「メールを作成して」と指示するか、独自に記入'}）
                                    </Typography>
                                    <TextField multiline fullWidth minRows={3} maxRows={12} size="small"
                                        value={draftOf(r)}
                                        onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: e.target.value }))}
                                        sx={{ mt: 0.5, '& .MuiInputBase-root': { fontSize: 13 } }} />
                                    <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center"
                                        sx={{ pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                                        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            処理
                                        </Typography>
                                        <Button size="small" variant="contained" startIcon={<SendIcon />}
                                            disabled={busyId === r.id || draftOf(r).trim() === ''}
                                            onClick={async () => {
                                                setCancelChoice(null); setCancelNote(''); setCarTypeChange('');
                                                setStatusChange(''); setResEmail(null);
                                                // 紐づき予約の顧客メールを取得し、宛先の既定を決める
                                                // (差出人が自社システム=LP通知なら顧客メールを既定に)
                                                const sAddr = senderAddress(r);
                                                let target: 'sender' | 'reservation' = 'sender';
                                                setGroupInfo(null);
                                                if (r.reservation_code) {
                                                    try {
                                                        const full = await fetchReservationByCode(r.reservation_code);
                                                        const em = (full?.customer_email || full?.detail?.mail || '').trim();
                                                        if (em) setResEmail(em);
                                                        if (!sAddr || OWN_DOMAIN_RE.test(sAddr)) target = 'reservation';
                                                        // 複数台グループなら件数を表示（ステータス・日時変更は全件に適用）
                                                        const grp = await fetchGroupReservations(r.reservation_code);
                                                        if (grp.length > 1) setGroupInfo({ count: grp.length, codes: grp.map((x: any) => String(x.contact_code)) });
                                                    } catch { /* 取得失敗時は sender のまま(サーバ側ガードあり) */ }
                                                }
                                                setSendTarget(target);
                                                setConfirmId(r.id);
                                            }}>
                                            送信する
                                        </Button>
                                        <Button size="small" color="secondary"
                                            disabled={draftOf(r).trim() === ''}
                                            onClick={() => setSaveTpl({
                                                decisionId: r.id, name: '',
                                                subject: r.mail_subject || `Re: ${r.subject || ''}`,
                                                body: draftOf(r),
                                            })}>
                                            テンプレとして保存
                                        </Button>
                                        <Button size="small" variant="outlined" color="inherit"
                                            disabled={busyId === r.id}
                                            onClick={() => setResolveDialog({ row: r, resolution: 'handled_offline', note: '' })}>
                                            別途対応済み
                                        </Button>
                                        <Button size="small" variant="outlined" color="inherit"
                                            disabled={busyId === r.id}
                                            onClick={() => setResolveDialog({ row: r, resolution: 'no_action', note: '' })}>
                                            対応不要
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* 処理済みの理由（AI学習用に記録されたもの） */}
                            {r.resolve_note && (
                                <Box sx={{ mt: 1, p: 1.25, background: '#fefce8', borderRadius: 2, borderLeft: '3px solid #eab308' }}>
                                    <Typography variant="caption" sx={{ color: '#a16207', fontWeight: 700 }}>
                                        対応の記録（{OUTCOME_LABELS[r.outcome || ''] || r.outcome}）
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>{r.resolve_note}</Typography>
                                </Box>
                            )}

                            {/* 処理済み表示 */}
                            {r.staff_reply && (
                                <Box sx={{ mt: 1, p: 1.5, background: '#f0fdf4', borderRadius: 2, borderLeft: '3px solid #10b981' }}>
                                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>送信済みの返信</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{r.staff_reply}</Typography>
                                </Box>
                            )}
                        </Paper>
                    );
    };

    // 送信確認の対象は「要対応タブの rows」と「受信メール統合ビューの pendingRows」の両方から探す。
    // （2026-08-08: 統合ビューのカードは pendingRows 由来のため rows だけ見ると
    //   ダイアログが開かず「送信するを押しても無反応」になっていた）
    const confirmRow = [...rows, ...pendingRows].find((r) => r.id === confirmId) || null;

    // 予約起点の起案: 予約コード + 指示 → AI が件名/本文を起案し「送信待ち」としてキューに載る
    const compose = async () => {
        setComposing(true);
        try {
            const json = await api('compose', {}, {
                reservation_code: composeCode.trim(),
                instruction: composeInstruction.trim(),
                operator: getOperatorName(),
            });
            setToast({ msg: `起案しました → 宛先 ${json.send_to}${json.note ? `（注: ${json.note}）` : ''}`, ok: true });
            setComposeInstruction('');
            setFilter('pending');
            load('pending');
        } catch (e: any) {
            setToast({ msg: `起案失敗: ${e.message}`, ok: false });
        }
        setComposing(false);
    };

    // 対話型の「指示 → AI起案」: 過去の指示と案(手動編集込み)を文脈として渡すマルチターン
    const draftReply = async (r: QueueDecision, override?: { instruction?: string; mode?: 'auto' | 'data' | 'mail' }) => {
        const instruction = (override?.instruction ?? (cardInstructions[r.id] || '')).trim();
        const mode = override?.mode ?? (aiMode[r.id] || 'auto');
        setDraftingId(r.id);
        try {
            // 履歴の最終ターンの body は「現在のエディタ内容」に差し替え(手動編集を AI に見せる)
            let history = [...(chatHistory[r.id] || [])];
            const current = draftOf(r).trim();
            if (history.length > 0) {
                history[history.length - 1] = { ...history[history.length - 1], body: current };
            } else if (current !== '') {
                history = [{ instruction: '(初稿。テンプレ適用または手動作成)', body: current }];
            }
            const json = await api('draft_reply', {}, {
                decision_id: r.id, instruction, history, mode, operator: getOperatorName(),
            });
            // メール本文が返ったときだけ下書きを差し替える（対話だけのターンで消さない）
            if (json.body) setDrafts((s) => ({ ...s, [r.id]: json.body }));
            setChatHistory((s) => ({ ...s, [r.id]: [...history, { instruction, body: json.body || draftOf(r), message: json.message || json.note || '' }] }));
            setCardInstructions((s) => ({ ...s, [r.id]: '' }));
            setAskMail((s) => ({ ...s, [r.id]: !!json.ask_mail }));
            // 予約変更の提案があれば、現在値を取得して差分表示の準備
            if (json.proposed_changes && r.reservation_code) {
                try {
                    const current = await fetchReservationByCode(r.reservation_code);
                    setProposals((s) => ({ ...s, [r.id]: { changes: json.proposed_changes, current } }));
                } catch { setProposals((s) => ({ ...s, [r.id]: { changes: json.proposed_changes, current: null } })); }
            } else if (json.proposed_changes && !r.reservation_code) {
                setToast({ msg: 'AI が予約変更を提案しましたが、予約が紐づいていないため適用できません。先に紐づけてください', ok: false });
            }
            setToast({
                msg: json.body
                    ? `メールを作成しました${json.template_used ? `（テンプレ: ${json.template_used}）` : ''}`
                    : 'AI が回答しました（メールは作成していません）',
                ok: true,
            });
        } catch (e: any) {
            setToast({ msg: `起案失敗: ${e.message}`, ok: false });
        }
        setDraftingId(null);
    };

    // メールアドレス / 予約コードで経緯を概要化
    const summarize = async (by: 'email' | 'code', value: string, title: string) => {
        setSummaryDialog('loading');
        try {
            const json = await api('summarize', {}, { by, value, operator: getOperatorName() });
            setSummaryDialog({ title: `${title}（メール${json.mails_found}件から要約）`, result: json.result });
        } catch (e: any) {
            setSummaryDialog(null);
            setToast({ msg: `概要化失敗: ${e.message}`, ok: false });
        }
    };

    const senderAddress = (r: QueueDecision) => {
        const m = (r.sender || '').match(/[^<\s"]+@[^>\s"]+/);
        return m ? m[0].replace(/[<>.,]+$/, '') : null;
    };

    // テンプレを選んで決定的に展開（AI 不使用。{変数} は予約実データでサーバ展開）
    const applyTemplate = async (r: QueueDecision, name: string) => {
        try {
            const json = await api('apply_template', {}, { decision_id: r.id, template_name: name });
            setDrafts((s) => ({ ...s, [r.id]: json.body }));
            setToast({ msg: `テンプレ「${name}」を適用しました${json.note ? `（${json.note}）` : ''}`, ok: true });
        } catch (e: any) {
            setToast({ msg: `テンプレ適用失敗: ${e.message}`, ok: false });
        }
    };

    // 添付一覧（Gmail から都度参照。DB/Drive に保存しない）
    const loadAttachments = async (r: QueueDecision) => {
        setAttachments((s) => ({ ...s, [r.id]: 'loading' }));
        try {
            const json = await api('attachments', { mail_id: r.mail_id });
            setAttachments((s) => ({ ...s, [r.id]: json.attachments || [] }));
            if ((json.attachments || []).length === 0) setToast({ msg: 'このメールに添付はありません', ok: true });
        } catch (e: any) {
            setAttachments((s) => ({ ...s, [r.id]: [] }));
            setToast({ msg: `添付一覧の取得失敗: ${e.message}`, ok: false });
        }
    };

    // 添付URLはサーバ発行の署名付き(期限30分)。api_key は URL に載せない(公開事故対策)
    // 期限切れに備え、開く直前に一覧を取り直してから開くヘルパ
    const openAttachment = (a: Attachment) => window.open(a.url, '_blank');

    // 日本語訳（表示用・保存しない）
    const translate = async (r: QueueDecision) => {
        setTranslations((s) => ({ ...s, [r.id]: 'loading' }));
        try {
            const json = await api('translate', {}, { decision_id: r.id });
            setTranslations((s) => ({ ...s, [r.id]: { subject: json.subject_ja, body: json.body_ja } }));
        } catch (e: any) {
            setTranslations((s) => { const c = { ...s }; delete c[r.id]; return c; });
            setToast({ msg: `翻訳失敗: ${e.message}`, ok: false });
        }
    };

    // 添付を Gemini で判定（本人名義の証明として有効か等。結果は判断理由に蓄積）
    const judgeAttachment = async (r: QueueDecision, a: Attachment) => {
        setJudgingAtt(a.attachment_id);
        try {
            const json = await api('judge_attachment', {}, {
                decision_id: r.id, attachment_id: a.attachment_id, mime: a.mime, filename: a.filename,
                instruction: (cardInstructions[r.id] || '').trim(),
                operator: getOperatorName(),
            });
            const v = json.verdict || {};
            const valid = v.is_valid_evidence === true ? '✅有効' : v.is_valid_evidence === false ? '❌無効' : '❓判断不能';
            setToast({
                msg: `判定[${a.filename}]: ${v.document_type || '?'} / ${valid}`
                    + `${(v.concerns || []).length ? ` / 懸念: ${(v.concerns || []).join('・')}` : ''}`,
                ok: true,
            });
            load(); // 判断理由に判定が追記されたので再読込
        } catch (e: any) {
            setToast({ msg: `AI判定失敗: ${e.message}`, ok: false });
        }
        setJudgingAtt(null);
    };

    // サイドパネル共通の定型テンプレ（リクエスト系・キャンセル系）を予約実データで生成
    const applyBuiltin = async (r: QueueDecision, type: string) => {
        try {
            const full = await fetchReservationByCode(r.reservation_code!);
            if (!full) { setToast({ msg: `予約 ${r.reservation_code} が見つかりません`, ok: false }); return; }
            const mail = buildBuiltinMail(type, toEditorReservation(full) as any, tplLang);
            if (!mail) return;
            setDrafts((s) => ({ ...s, [r.id]: mail.body }));
            const label = BUILTIN_MAIL_TEMPLATES.find((t) => t.key === type)?.label || type;
            setToast({ msg: `定型「${label}」を適用しました（${tplLang}）`, ok: true });
        } catch (e: any) {
            setToast({ msg: `定型の適用に失敗: ${e.message}`, ok: false });
        }
    };

    // Square 請求書の取消は**サーバ側**(send_reply のキャンセル処理)で行う。
    // 予約の status 変更と invoice_link クリアを1回の POST にまとめ、競合と巻き戻しを防ぐ。

    // 差出人ルールの登録/解除（差出人集計タブ。決定的ルール = 入力層ガードレール）
    const setRule = async (s: SenderAgg, rule: string) => {
        try {
            if (rule === '') {
                await api('delete_rule', {}, { pattern: s.rule_pattern || s.address, operator: getOperatorName() });
                setToast({ msg: `${s.address} のルールを解除しました`, ok: true });
            } else {
                await api('set_rule', {}, {
                    pattern: s.address, match_type: 'address', rule, operator: getOperatorName(),
                });
                setToast({ msg: `${s.address} → ${RULE_LABELS[rule]}`, ok: true });
            }
            load('senders');
        } catch (e: any) {
            setToast({ msg: `ルール更新失敗: ${e.message}`, ok: false });
        }
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 1, md: 3 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                <IconButton onClick={() => navigate('/dashboard')}><ArrowBackIcon /></IconButton>
                <Typography variant="h6" fontWeight={800}>メール受信箱キュー</Typography>
                {counts && (
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        要対応 {counts.pending ?? 0} / 処理済 {counts.done ?? 0} / 全 {counts.total ?? 0}
                    </Typography>
                )}
                <Box ml="auto" display="flex" gap={1}>
                    <Button size="small" startIcon={<CloudDownloadIcon />} variant="outlined"
                        disabled={fetching} onClick={manualFetch}>
                        {fetching ? '取り込み中…' : 'Gmailから取得'}
                    </Button>
                    <IconButton size="small" onClick={() => load()}><RefreshIcon /></IconButton>
                </Box>
            </Box>

            {/* Filter tabs */}
            <ToggleButtonGroup exclusive size="small" value={filter} sx={{ mb: 2 }}
                onChange={(_, v) => { if (v) { setFilter(v); } }}>
                <ToggleButton value="inbox">受信メール{counts && Number(counts.pending) > 0 ? `（要対応 ${counts.pending}）` : ''}</ToggleButton>
                <ToggleButton value="done">処理済み</ToggleButton>
                <ToggleButton value="all">AI判断済み</ToggleButton>
                <ToggleButton value="senders">差出人集計</ToggleButton>
            </ToggleButtonGroup>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* 予約起点の起案（指示 → AI起案 → 送信待ちカード → 承認送信） */}
            {filter === 'inbox' && (
                <Paper sx={{ p: 2, mb: 2, border: '1px dashed #7c3aed', background: '#fdfaff' }}>
                    <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 800 }}>
                        ✏️ 予約起点でメールを起案（例: 「基本料金を6000円アップでデリカをオファーするメールを送る」）
                    </Typography>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="flex-start">
                        <TextField size="small" label="予約コード" value={composeCode}
                            onChange={(e) => setComposeCode(e.target.value)}
                            placeholder="TR-XXXXXX-XXXXXX" sx={{ width: 210 }} />
                        <TextField size="small" label="指示" value={composeInstruction} multiline maxRows={4}
                            onChange={(e) => setComposeInstruction(e.target.value)}
                            sx={{ flex: 1, minWidth: 260 }} />
                        <Button variant="contained" disabled={composing || !composeCode.trim() || !composeInstruction.trim()}
                            onClick={compose}>
                            {composing ? '起案中…' : 'AI起案'}
                        </Button>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                        宛先は予約の顧客メールに自動設定。起案しても送信はされません（下のカードで確認・編集して送信）
                    </Typography>
                </Paper>
            )}

            {/* 受信メール一覧（取り込んだ全メール。初期運用=取得のみ、の観察用） */}
            {filter === 'inbox' && (
                <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                        <ToggleButtonGroup exclusive size="small" value={mailScope}
                            onChange={(_, v) => { if (v) setMailScope(v); }}>
                            <ToggleButton value="inbox">受信トレイ</ToggleButton>
                            <ToggleButton value="all">すべて（アーカイブ含む）</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {mailScope === 'inbox' ? 'Gmail の受信トレイと同期' : '取り込み済み全件'}: {mailTotal} 件（新しい順・最大100件）
                        </Typography>
                    </Box>
                    {mails.map((m) => {
                        const pendingDec = pendingRows.find((d) => d.mail_id === m.mail_id && d.outcome === null);
                        return (
                        <Box key={m.id}>
                        <Paper sx={{ p: 1.5, border: '1px solid #e2e8f0',
                            ...(pendingDec ? { borderColor: '#fca5a5', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}) }}>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <IconButton size="small" sx={{ p: 0.25 }}
                                    onClick={async () => {
                                        const next = !(Number(m.is_starred) === 1);
                                        try {
                                            await api('star', {}, { mail_id: m.mail_id, starred: next });
                                            setMails((s) => s.map((x) => x.mail_id === m.mail_id ? { ...x, is_starred: next ? 1 : 0 } : x));
                                        } catch (e: any) { setToast({ msg: `スター変更失敗: ${e.message}`, ok: false }); }
                                    }}>
                                    <Typography sx={{ fontSize: 16, lineHeight: 1 }}>{Number(m.is_starred) === 1 ? '⭐' : '☆'}</Typography>
                                </IconButton>
                                <Typography variant="caption" sx={{ color: '#64748b', minWidth: 110 }}>
                                    {m.received_at ? new Date(m.received_at.replace(/-/g, '/')).toLocaleString('ja-JP') : ''}
                                </Typography>
                                <Typography variant="body2"
                                    onClick={pendingDec
                                        ? () => setOpenCards((s) => ({ ...s, [pendingDec.id]: !s[pendingDec.id] }))
                                        : () => toggleMailBody(m.mail_id)}
                                    sx={{ flex: 1, minWidth: 200, fontWeight: Number(m.is_unread) === 1 ? 800 : 500,
                                        cursor: 'pointer' }}>
                                    {m.subject || '(件名なし)'}
                                </Typography>
                                {m.is_ota === 1 && (
                                    <Chip label={`OTA:${m.ota_type}`} size="small" variant="outlined"
                                        sx={{ fontSize: '10px', height: 20 }} />
                                )}
                                {pendingDec && (
                                    <Chip label={openCards[pendingDec.id] ? '要対応 ▲' : '要対応 ▼'} size="small" clickable
                                        onClick={() => setOpenCards((s) => ({ ...s, [pendingDec.id]: !s[pendingDec.id] }))}
                                        sx={{ background: '#ef4444', color: 'white', fontWeight: 800, fontSize: '10px', height: 20 }} />
                                )}
                                {!pendingDec && m.ai_action && (
                                    <Chip label={(ACTION_STYLE[m.ai_action]?.label) || m.ai_action} size="small"
                                        sx={{ background: (ACTION_STYLE[m.ai_action]?.color) || '#e2e8f0', color: 'white', fontWeight: 700, fontSize: '10px', height: 20 }} />
                                )}
                                {!pendingDec && !m.decision_id && m.is_ota === 0 && (
                                    <Button size="small" variant="outlined" color="error"
                                        sx={{ fontSize: 10, py: 0, minWidth: 0, px: 0.75 }}
                                        onClick={async () => {
                                            try {
                                                const json = await api('promote', {}, { mail_id: m.mail_id, operator: getOperatorName() });
                                                setToast({ msg: `要対応に追加しました${json.reservation_code ? `（予約 ${json.reservation_code}）` : ''}`, ok: true });
                                                load('inbox');
                                            } catch (e: any) { setToast({ msg: `追加失敗: ${e.message}`, ok: false }); }
                                        }}>
                                        要対応へ
                                    </Button>
                                )}
                                {Number(m.is_inbox) === 1 && (
                                    <Button size="small" variant="outlined" color="inherit"
                                        sx={{ fontSize: 10, py: 0, minWidth: 0, px: 0.75 }}
                                        onClick={async () => {
                                            try {
                                                const j = await api('archive', {}, { mail_id: m.mail_id, operator: getOperatorName() });
                                                setToast({ msg: `アーカイブしました${j.closed_decisions ? '（未処理の判断もクローズ）' : ''}`, ok: true });
                                                load('inbox');
                                            } catch (e: any) { setToast({ msg: `アーカイブ失敗: ${e.message}`, ok: false }); }
                                        }}>
                                        アーカイブ
                                    </Button>
                                )}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', ml: 3.5 }}>{m.sender}</Typography>
                            {/* 要対応でなくても本文を読めるようにする（件名クリックで開閉） */}
                            {!pendingDec && (
                                <Box sx={{ ml: 3.5, mt: 0.25 }}>
                                    {mailBodies[m.mail_id] === undefined ? (
                                        <Typography variant="caption" sx={{ color: '#94a3b8', cursor: 'pointer' }}
                                            onClick={() => toggleMailBody(m.mail_id)}>
                                            ▼ 本文を表示{m.snippet ? `　${String(m.snippet).slice(0, 60)}…` : ''}
                                        </Typography>
                                    ) : mailBodies[m.mail_id] === 'loading' ? (
                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>本文を取得中…</Typography>
                                    ) : (
                                        <Box sx={{ p: 1.5, mt: 0.5, background: '#f8fafc', borderRadius: 2, borderLeft: '3px solid #cbd5e1' }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#334155', maxHeight: 400, overflow: 'auto' }}>
                                                {mailBodies[m.mail_id] as string}
                                            </Typography>
                                            <Box display="flex" gap={0.5} mt={0.75} flexWrap="wrap" alignItems="center">
                                                <Button size="small" sx={{ fontSize: 11 }} onClick={() => toggleMailBody(m.mail_id)}>▲ 閉じる</Button>
                                                {mailAtts[m.mail_id] === undefined && (
                                                    <Button size="small" sx={{ fontSize: 11 }} onClick={() => loadMailAttachments(m.mail_id)}>📎 添付を表示</Button>
                                                )}
                                                {mailAtts[m.mail_id] === 'loading' && (
                                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>添付を確認中…</Typography>
                                                )}
                                                {Array.isArray(mailAtts[m.mail_id]) && (mailAtts[m.mail_id] as Attachment[]).map((a) => (
                                                    <Chip key={a.attachment_id} size="small" label={`📎 ${a.filename}`} clickable
                                                        onClick={() => openAttachment(a)} sx={{ fontSize: 11, height: 22 }} />
                                                ))}
                                            </Box>
                                            {Array.isArray(mailAtts[m.mail_id]) &&
                                                (mailAtts[m.mail_id] as Attachment[]).filter((a) => a.mime.startsWith('image/')).map((a) => (
                                                    <img key={a.attachment_id} src={a.url} alt={a.filename}
                                                        style={{ display: 'block', maxWidth: '100%', maxHeight: 360, borderRadius: 8, marginTop: 8, cursor: 'zoom-in' }}
                                                        onClick={() => openAttachment(a)} />
                                                ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                        {/* 要対応 decision はその場に展開（統合ビュー・既定は畳む） */}
                        {pendingDec && !openCards[pendingDec.id] && (
                            <Box onClick={() => setOpenCards((s) => ({ ...s, [pendingDec.id]: true }))}
                                sx={{ px: 1.5, py: 0.75, border: '1px solid #fca5a5', borderTop: 'none',
                                    borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
                                    background: '#fef2f2', cursor: 'pointer' }}>
                                <Typography variant="caption" sx={{ color: '#b91c1c' }} noWrap display="block">
                                    ▼ {(pendingDec.ai_reasoning || '').slice(0, 90)}
                                    {(pendingDec.ai_reasoning || '').length > 90 ? '…' : ''}
                                </Typography>
                            </Box>
                        )}
                        {pendingDec && (
                            <Collapse in={!!openCards[pendingDec.id]} unmountOnExit>
                                <Box sx={{ border: '1px solid #fca5a5', borderTop: 'none',
                                    borderBottomLeftRadius: 8, borderBottomRightRadius: 8, overflow: 'hidden' }}>
                                    {renderCard(pendingDec)}
                                </Box>
                            </Collapse>
                        )}
                        </Box>
                        );
                    })}
                    {!loading && mails.length === 0 && (
                        <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                            まだ取り込みがありません。「Gmailから取得」を押してください
                        </Typography>
                    )}
                </Box>
            )}

            {/* 差出人集計（「確実にアーカイブするもの」の特定用） */}
            {filter === 'senders' && (
                <Paper sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                        一般メール（非OTA）の差出人別件数。件数が多く定型的なものから
                        「返信不要」「アーカイブ」のルール化候補にする
                    </Typography>
                    {senders.map((s) => (
                        <Box key={s.address} display="flex" gap={1.5} alignItems="center"
                            sx={{ py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography variant="body2" fontWeight={800} sx={{ minWidth: 44, textAlign: 'right' }}>
                                {s.cnt}件
                            </Typography>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" fontWeight={700} noWrap>{s.address}</Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }} noWrap display="block">
                                    直近: {s.last_subject}（{s.last_received}）
                                </Typography>
                            </Box>
                            <ToggleButtonGroup exclusive size="small" value={s.rule || ''}
                                onChange={(_, v) => { if (v !== null) setRule(s, v); }}
                                sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: 11 } }}>
                                <ToggleButton value="ignore">無視</ToggleButton>
                                <ToggleButton value="escalate">エスカレ</ToggleButton>
                                <ToggleButton value="ai">AI対象</ToggleButton>
                                <ToggleButton value="">なし</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    ))}
                    {!loading && senders.length === 0 && (
                        <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 2 }}>
                            データがありません
                        </Typography>
                    )}
                </Paper>
            )}

            {/* Cards */}
            {filter !== 'inbox' && filter !== 'senders' && (
            <Box display="flex" flexDirection="column" gap={2}>
                {rows.map(renderCard)}
                {!loading && rows.length === 0 && (
                    <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                        {filter === 'pending' ? '要対応のメールはありません 🎉' : 'データがありません'}
                    </Typography>
                )}
            </Box>
            )}

            {/* 送信確認ダイアログ（3点セットの「確認」） */}
            <Dialog open={confirmRow !== null} onClose={() => setConfirmId(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>返信を送信しますか？</DialogTitle>
                <DialogContent>
                    {confirmRow && (
                        <>
                            {confirmRow.send_to ? (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    宛先は予約の顧客メール（サーバ側で固定）: {confirmRow.send_to}
                                </Alert>
                            ) : confirmRow.reservation_code && resEmail
                                && senderAddress(confirmRow)
                                && resEmail.toLowerCase() === senderAddress(confirmRow)!.toLowerCase() ? (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    宛先（サーバ側で固定）: {resEmail}
                                    <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>
                                        差出人と予約データのメールが一致しています
                                    </Typography>
                                </Alert>
                            ) : confirmRow.reservation_code && resEmail ? (
                                <Box sx={{ mb: 1.5, p: 1.5, background: '#f0f9ff', borderRadius: 2, border: '1px solid #7dd3fc' }}>
                                    <Typography variant="body2" fontWeight={800} sx={{ color: '#0369a1' }}>宛先の選択（実アドレスはサーバが解決）</Typography>
                                    <RadioGroup value={sendTarget} onChange={(e) => setSendTarget(e.target.value as any)}>
                                        <FormControlLabel value="sender" control={<Radio size="small" />}
                                            disabled={!!senderAddress(confirmRow) && OWN_DOMAIN_RE.test(senderAddress(confirmRow)!)}
                                            label={`元メールの差出人: ${senderAddress(confirmRow) || '(不明)'}${
                                                senderAddress(confirmRow) && OWN_DOMAIN_RE.test(senderAddress(confirmRow)!) ? '（自社システム — 送信不可）' : ''}`} />
                                        <FormControlLabel value="reservation" control={<Radio size="small" />}
                                            label={`予約データのメール: ${resEmail}`} />
                                    </RadioGroup>
                                    {senderAddress(confirmRow) && !OWN_DOMAIN_RE.test(senderAddress(confirmRow)!)
                                        && resEmail.toLowerCase() !== senderAddress(confirmRow)!.toLowerCase() && (
                                        <Alert severity="warning" sx={{ mt: 0.5 }}>
                                            ⚠ 差出人と予約データのメールアドレスが一致しません。本人確認のうえ宛先を選んでください
                                        </Alert>
                                    )}
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    宛先は元メールの差出人（サーバ側で固定）: {confirmRow.sender}
                                </Alert>
                            )}
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                件名: {confirmRow.send_to ? confirmRow.mail_subject : `Re: ${confirmRow.subject}`}
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, mt: 1, maxHeight: 220, overflow: 'auto' }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{draftOf(confirmRow)}</Typography>
                            </Paper>
                            {/* 車種変更の適用（オファー了承時など。中央経路で保存し、車両はパネルで人が確認） */}
                            {confirmRow.reservation_code && vehicleTypeList.length > 0 && (
                                <Box sx={{ mt: 2, p: 1.5, background: '#eff6ff', borderRadius: 2, border: '1px solid #93c5fd' }}>
                                    <Typography variant="body2" fontWeight={800} sx={{ color: '#1d4ed8', mb: 0.5 }}>
                                        予約 {confirmRow.reservation_code} への変更を同時に適用（任意）
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        <TextField select size="small" label="車種変更" value={carTypeChange}
                                            SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                                            onChange={(e) => setCarTypeChange(e.target.value)}
                                            sx={{ minWidth: 200 }}>
                                            <option value="">変更しない</option>
                                            {vehicleTypeList.map((vt: any) => (
                                                <option key={vt.code || vt.id} value={vt.code}>{vt.label}</option>
                                            ))}
                                        </TextField>
                                        <TextField select size="small" label="ステータス変更" value={statusChange}
                                            SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                                            onChange={(e) => setStatusChange(e.target.value)}
                                            sx={{ minWidth: 200 }}>
                                            <option value="">変更しない</option>
                                            {reservationStatusList.map((st: any) => (
                                                <option key={st.id} value={String(st.id)}>{st.label}</option>
                                            ))}
                                        </TextField>
                                    </Box>
                                    {reservationStatusList.length === 0 && (
                                        <Alert severity="warning" sx={{ mt: 0.75, py: 0 }}>
                                            ステータス一覧を取得できていません（変更を適用できません）
                                        </Alert>
                                    )}
                                    {groupInfo && (
                                        <Alert severity="info" sx={{ mt: 0.75, py: 0 }}>
                                            複数台予約グループ（{groupInfo.count}件）: {groupInfo.codes.join(' / ')}
                                            <br />ステータス・日時の変更は<b>グループ全件</b>に適用されます（車種は指定の1件のみ）
                                        </Alert>
                                    )}
                                    {(carTypeChange || statusChange) && (
                                        <Typography variant="caption" display="block" sx={{ color: '#1d4ed8', mt: 0.5 }}>
                                            送信後に予約を変更し、確認のため予約パネルを開きます
                                            {statusChange && ' — 例: オファー送信なら「オファー中」で枠を確保'}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {asksCancel(confirmRow) && (
                                <Box sx={{ mt: 2, p: 1.5, background: '#fff7ed', borderRadius: 2, border: '1px solid #fdba74' }}>
                                    <Typography variant="body2" fontWeight={800} sx={{ color: '#c2410c' }}>
                                        予約 {confirmRow.reservation_code} のステータスをキャンセル済みにしますか？
                                    </Typography>
                                    <RadioGroup row value={cancelChoice || ''}
                                        onChange={(e) => setCancelChoice(e.target.value as 'yes' | 'no')}>
                                        <FormControlLabel value="yes" control={<Radio size="small" />} label="はい（キャンセル済みに変更）" />
                                        <FormControlLabel value="no" control={<Radio size="small" />} label="いいえ（変更しない）" />
                                    </RadioGroup>
                                    {cancelChoice === 'no' && (
                                        <TextField size="small" fullWidth label="変更しない理由（AIの学習に使われます）"
                                            value={cancelNote} onChange={(e) => setCancelNote(e.target.value)}
                                            placeholder="例: 欠航証明の確認待ちのため保留" sx={{ mt: 0.5 }} />
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmId(null)}>キャンセル</Button>
                    <Button variant="contained" startIcon={<SendIcon />}
                        disabled={busyId !== null || (confirmRow !== null && asksCancel(confirmRow) && cancelChoice === null)}
                        onClick={() => confirmRow && send(confirmRow)}>
                        送信する
                    </Button>
                </DialogActions>
            </Dialog>

            {/* テンプレとして保存 */}
            <Dialog open={saveTpl !== null} onClose={() => setSaveTpl(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>テンプレとして保存</DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5 }}>
                        顧客名・日付・予約コードは保存時に {'{customer_name}'} 等の変数へ自動で置き換わります（予約紐づけ時）。
                        サイドパネルのメール生成でも同じテンプレが使えます。
                    </Typography>
                    <TextField size="small" fullWidth label="テンプレ名（例: キャンセル了承）" value={saveTpl?.name || ''}
                        onChange={(e) => setSaveTpl((s) => s && { ...s, name: e.target.value })} sx={{ mb: 1.5 }} />
                    <TextField size="small" fullWidth label="件名" value={saveTpl?.subject || ''}
                        onChange={(e) => setSaveTpl((s) => s && { ...s, subject: e.target.value })} sx={{ mb: 1.5 }} />
                    <TextField size="small" fullWidth multiline minRows={6} maxRows={14} label="本文" value={saveTpl?.body || ''}
                        onChange={(e) => setSaveTpl((s) => s && { ...s, body: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveTpl(null)}>キャンセル</Button>
                    <Button variant="contained"
                        disabled={!saveTpl?.name.trim() || !saveTpl?.subject.trim() || !saveTpl?.body.trim()}
                        onClick={async () => {
                            if (!saveTpl) return;
                            try {
                                const json = await api('save_template', {}, {
                                    name: saveTpl.name.trim(), subject: saveTpl.subject.trim(), body: saveTpl.body,
                                    decision_id: saveTpl.decisionId, operator: getOperatorName(),
                                });
                                setToast({
                                    msg: `テンプレ「${json.name}」を保存しました`
                                        + `${json.overwrote ? '（上書き）' : ''}${json.deexpanded ? '（変数へ自動置換済み）' : ''}`,
                                    ok: true,
                                });
                                setSaveTpl(null);
                                api('templates').then((j) => setTemplates(j.templates || [])).catch(() => {});
                            } catch (e: any) {
                                setToast({ msg: `テンプレ保存失敗: ${e.message}`, ok: false });
                            }
                        }}>
                        保存
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 「別途対応済み/対応不要」の理由入力（AI学習用） */}
            <Dialog open={resolveDialog !== null} onClose={() => setResolveDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {resolveDialog ? OUTCOME_LABELS[resolveDialog.resolution] : ''} にする
                </DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5 }}>
                        どう対応したか / なぜ返信不要かを残すと、次回から AI が同種のメールで同じ判断を学習します。
                        （例: 「メールアドレスの typo を修正し、Square から請求書を再送した」「広告メールのため対応不要」）
                    </Typography>
                    <TextField autoFocus fullWidth multiline minRows={3} size="small" label="対応の記録（任意・AIの学習に使われます）"
                        value={resolveDialog?.note || ''}
                        onChange={(e) => setResolveDialog((s) => s && { ...s, note: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveDialog(null)}>キャンセル</Button>
                    <Button variant="contained" disabled={busyId !== null}
                        onClick={() => {
                            if (!resolveDialog) return;
                            const { row, resolution, note } = resolveDialog;
                            setResolveDialog(null);
                            resolve(row, resolution, note.trim());
                        }}>
                        記録して完了
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 概要化ダイアログ */}
            <Dialog open={summaryDialog !== null} onClose={() => setSummaryDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {summaryDialog === 'loading' ? '概要を作成中…' : (summaryDialog as any)?.title || ''}
                </DialogTitle>
                <DialogContent>
                    {summaryDialog === 'loading' && <LinearProgress />}
                    {summaryDialog !== null && summaryDialog !== 'loading' && (() => {
                        const v = (summaryDialog as any).result || {};
                        return (
                            <>
                                <Typography variant="body2" sx={{ mb: 1.5 }}>{v.summary}</Typography>
                                {(v.timeline || []).length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                                        {(v.timeline || []).map((t: any, i: number) => (
                                            <Typography key={i} variant="caption" display="block" sx={{ color: '#334155' }}>
                                                <b>{t.date}</b> {t.event}
                                            </Typography>
                                        ))}
                                    </Paper>
                                )}
                                {v.current_state && (
                                    <Alert severity="info" sx={{ mb: 1 }}>{v.current_state}</Alert>
                                )}
                                {(v.pending || []).length > 0 && (
                                    <Alert severity="warning">
                                        未対応: {(v.pending || []).join(' / ')}
                                    </Alert>
                                )}
                            </>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSummaryDialog(null)}>閉じる</Button>
                </DialogActions>
            </Dialog>

            {/* 予約サイドパネル（LineChatBoard と同じ埋め込み。保存は中央経路 saveDispatchReservation のみ） */}
            <ReservationEditorPanel
                {...{
                    open: !!selectedRes,
                    editing: editingRes,
                    disabled: isSavingRes,
                    onCancel: () => setSelectedRes(null),
                    onClose: () => setSelectedRes(null),
                    onChange: (updates: any) => setEditingRes((prev: any) => ({ ...prev, ...updates })),
                    onSave: async (updated: any, options: any = {}) => {
                        const { keepOpen = false } = options;
                        if (isSavingRes) return;
                        setIsSavingRes(true);
                        try {
                            const saved = await saveDispatchReservation(updated || editingRes);
                            if (saved?.contact_code) resCache.current[String(saved.contact_code)] = saved;
                            setToast({ msg: '予約を保存しました', ok: true });
                            if (!keepOpen) setSelectedRes(null); else setSelectedRes(saved);
                        } catch (e: any) {
                            setToast({ msg: `予約の保存に失敗: ${e?.message || e}`, ok: false });
                        }
                        setIsSavingRes(false);
                    },
                    registerNameInputRef: () => {},
                    reservationStatusList,
                    vehicles: vehiclesCache,
                    vehicleTypes: vehicleTypeList,
                    onAssignClick: () => {},
                } as any}
            />

            {/* 失敗(⚠)を含む通知は長く表示する（2026-08-08: キャンセル失敗の警告が5秒で消え見逃された） */}
            <Snackbar open={toast !== null}
                autoHideDuration={toast && (!toast.ok || toast.msg.includes('⚠')) ? 20000 : 5000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={toast && (!toast.ok || toast.msg.includes('⚠')) ? 'error' : 'success'}
                    onClose={() => setToast(null)} sx={{ maxWidth: 640 }}>
                    {toast?.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
